import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { useActionPopoverContext } from 'op-contexts';
import {
  Stack,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListSubheader,
  useTheme,
  Tooltip,
  InputBase,
  SvgIcon,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AddCircleOutline as AddIcon,
} from '@mui/icons-material';
import { ChatHistoryItem } from '../Interface';
import {
  getTooltipSlotProps,
  menuItemSx,
  menuIconSx,
  scrollbarSx,
  getConversationItemSx,
  truncatedTextSx,
} from '../Chat.styles';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar';
import NewChatIcon from 'shared-components/images/newchat.svg?react';

interface ChatConversationListProps {
  chatHistory: ChatHistoryItem[];
  value: number;
  selectedConversationId?: number | null;
  onConversationClick: (index: number, conversationId?: number) => void;
  onMenuOpen: (e: React.MouseEvent<HTMLElement>, index: number) => void;
  onMenuClose: () => void;
  menuAnchor: { el: HTMLElement; index: number } | null;
  onRename: (conversationId: number, newTitle: string) => Promise<boolean>;
  onDeleteClick: () => void;
  editingConversationId: number | null;
  setEditingConversationId: (id: number | null) => void;
  limitWidth?: boolean;
}

interface ConversationItemProps {
  item: ChatHistoryItem;
  index: number;
  isSelected: boolean;
  isMenuOpen: boolean;
  isEditing: boolean;
  onConversationClick: (index: number, conversationId?: number) => void;
  onMenuOpen: (e: React.MouseEvent<HTMLElement>, index: number) => void;
  onMenuClose: () => void;
  menuAnchor: { el: HTMLElement; index: number } | null;
  onStartEdit: () => void;
  onSaveEdit: (newTitle: string) => void;
  onCancelEdit: () => void;
  onDeleteClick: () => void;
}

/**
 * Individual conversation item with inline editing support
 * Memoized to prevent unnecessary re-renders when other conversations change
 */
const ConversationItem: React.FC<ConversationItemProps> = memo(
  ({
    item,
    index,
    isSelected,
    isMenuOpen,
    isEditing,
    onConversationClick,
    onMenuOpen,
    onMenuClose,
    menuAnchor,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDeleteClick,
  }) => {
    const theme = useTheme();
    const textRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const [editValue, setEditValue] = useState(item.title);
    const { popoverContainerEl } = useActionPopoverContext();

    // Check if text is truncated whenever title changes or on mount
    useEffect(() => {
      const checkTruncation = () => {
        const element = textRef.current;
        if (element) {
          setIsTruncated(element.scrollWidth > element.clientWidth);
        }
      };

      checkTruncation();
      window.addEventListener('resize', checkTruncation);
      return () => window.removeEventListener('resize', checkTruncation);
    }, [item.title]);

    // Focus and select input when entering edit mode
    useEffect(() => {
      if (isEditing && inputRef.current) {
        setEditValue(item.title);
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing, item.title]);

    const handleClick = useCallback(() => {
      // Don't select conversation if menu is open (user is clicking away to close menu)
      // or if editing is in progress
      if (!isEditing && !isMenuOpen) {
        onConversationClick(index, item.conversationId);
      }
    }, [onConversationClick, index, item.conversationId, isEditing, isMenuOpen]);

    const handleMenuClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onMenuOpen(e, index);
      },
      [onMenuOpen, index],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        // Stop propagation to prevent parent Box from handling space/enter keys
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          if (editValue.trim()) {
            onSaveEdit(editValue.trim());
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancelEdit();
        }
      },
      [editValue, onSaveEdit, onCancelEdit],
    );

    const handleBlur = useCallback(() => {
      if (editValue.trim() && editValue.trim() !== item.title) {
        onSaveEdit(editValue.trim());
      } else {
        onCancelEdit();
      }
    }, [editValue, item.title, onSaveEdit, onCancelEdit]);

    const handleInputClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const isNewConversation = item.kind === 'new';

    const textContent = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {isNewConversation && <SvgIcon color="primary" component={NewChatIcon} fontSize="small" />}
        <Typography ref={textRef} component="span" variant="body2" sx={truncatedTextSx}>
          {isNewConversation ? 'New conversation' : item.title}
        </Typography>
      </Box>
    );

    const editInput = (
      <InputBase
        inputRef={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onClick={handleInputClick}
        autoFocus
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: '14px',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 0',
          '& input': {
            padding: 0,
            caretColor: theme.palette.text.primary,
          },
        }}
      />
    );

    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        sx={getConversationItemSx(theme, isSelected, isMenuOpen, !!item.conversationId)}>
        {isEditing ? (
          editInput
        ) : isTruncated ? (
          <Tooltip
            title={item.title}
            placement="bottom"
            arrow
            enterDelay={500}
            leaveDelay={0}
            disableInteractive
            slotProps={getTooltipSlotProps(theme, popoverContainerEl)}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>{textContent}</Box>
          </Tooltip>
        ) : (
          textContent
        )}
        {item.conversationId && !isEditing && (
          <>
            <IconButton
              className={`menu-button${isMenuOpen ? ' menu-open' : ''}`}
              size="small"
              onClick={handleMenuClick}
              aria-label="Conversation options"
              sx={{
                opacity: isMenuOpen ? 1 : 0,
                transition: 'opacity 0.2s',
                padding: '4px',
                marginLeft: '4px',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor?.el}
              open={menuAnchor !== null && menuAnchor.index === index}
              onClose={onMenuClose}
              disablePortal={Boolean(popoverContainerEl)}
              container={popoverContainerEl ?? undefined}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  sx: {
                    width: '120px',
                    minWidth: '120px',
                    marginLeft: '10px',
                    marginTop: '-8px',
                  },
                },
              }}>
              <MenuItem onClick={onStartEdit} sx={menuItemSx(theme)}>
                <EditIcon sx={menuIconSx(theme)} />
                Rename
              </MenuItem>
              <MenuItem onClick={onDeleteClick} sx={menuItemSx(theme)}>
                <DeleteIcon sx={{ ...menuIconSx(theme), color: theme.palette.error.main }} />
                <Typography sx={{ color: theme.palette.error.main, fontSize: '14px' }}>Delete</Typography>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    );
  },
  // Custom comparison to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    return (
      prevProps.item.title === nextProps.item.title &&
      prevProps.item.conversationId === nextProps.item.conversationId &&
      prevProps.item.kind === nextProps.item.kind &&
      prevProps.index === nextProps.index &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isMenuOpen === nextProps.isMenuOpen &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.menuAnchor === nextProps.menuAnchor
    );
  },
);

export const ChatConversationList: React.FC<ChatConversationListProps> = memo(
  ({
    chatHistory,
    value,
    selectedConversationId,
    onConversationClick,
    onMenuOpen,
    onMenuClose,
    menuAnchor,
    onRename,
    onDeleteClick,
    editingConversationId,
    setEditingConversationId,
    limitWidth = false,
  }) => {
    const [renameErrorSnackbarOpen, setRenameErrorSnackbarOpen] = useState(false);

    const handleStartEdit = useCallback(
      (conversationId: number) => {
        onMenuClose();
        setEditingConversationId(conversationId);
      },
      [onMenuClose, setEditingConversationId],
    );

    const handleSaveEdit = useCallback(
      async (conversationId: number, newTitle: string) => {
        const success = await onRename(conversationId, newTitle);
        if (success) {
          setEditingConversationId(null);
        } else {
          // Keep edit mode active and show error snackbar
          setRenameErrorSnackbarOpen(true);
        }
      },
      [onRename, setEditingConversationId],
    );

    const handleCancelEdit = useCallback(() => {
      setEditingConversationId(null);
    }, [setEditingConversationId]);

    return (
      <>
        <Stack direction="column" width={limitWidth ? '260px' : 'auto'} height="100%" padding="16px" sx={scrollbarSx}>
          {chatHistory.map((item, index) => {
            // Render separators as headers
            if (item.kind === 'separator') {
              return <ListSubheader key={index}>{item.title}</ListSubheader>;
            }

            // Render "New Conversation" and regular conversations as clickable buttons
            const isMenuOpen = menuAnchor !== null && menuAnchor.index === index;
            const isSelected =
              (item.conversationId && selectedConversationId === item.conversationId) ||
              (!item.conversationId && value === index);
            const isEditing = item.conversationId === editingConversationId;

            return (
              <ConversationItem
                key={item.conversationId || index}
                item={item}
                index={index}
                isSelected={isSelected}
                isMenuOpen={isMenuOpen}
                isEditing={isEditing}
                onConversationClick={onConversationClick}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                menuAnchor={menuAnchor}
                onStartEdit={() => item.conversationId && handleStartEdit(item.conversationId)}
                onSaveEdit={(newTitle) => item.conversationId && handleSaveEdit(item.conversationId, newTitle)}
                onCancelEdit={handleCancelEdit}
                onDeleteClick={onDeleteClick}
              />
            );
          })}
        </Stack>
        <NotificationSnackbar
          open={renameErrorSnackbarOpen}
          onClose={() => setRenameErrorSnackbarOpen(false)}
          type="error"
          message="Failed to rename conversation. Please try again."
        />
      </>
    );
  },
);
